import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { catchError, map } from 'rxjs/operators';
import { NgRedux } from '@angular-redux/store';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastaService } from 'ngx-toasta';
import { forkJoin, Observable, of } from 'rxjs';
import { AuthenticationService } from '../../../../modules/authentication/authentication.service';
import { NexiaBasketAction } from '../action/nexia-basket.action';
import { DocumentDetailsService } from '../../details/service/document/document-details.service';
import { NexiaBasketObject, NexiaBasketObjectKeys } from '../../../models/nexia-basket-object';
import { IAppState } from '../../../../store/model';
import { NexiaBasketState } from '../reducer/nexia-basket.reducer';
import { DocumentDetails } from '../../../models/document-details';
import { Entity } from '../../../models/Entity';
import { DELETE_MODE } from '../../details/service/abstract-details.service';
import { HttpErrorInterceptor } from '../../../../modules/http-error-interceptor/http-error-interceptor';
import { Url } from '../../../models/url';
import { ConfigurationActions } from '../../../../modules/configuration/configuration.actions';

@Injectable({
  providedIn: 'root'
})
export class NexiaBasketService {

  constructor(
    private ngRedux: NgRedux<IAppState>,
    private nexiaBasketAction: NexiaBasketAction,
    private documentDetailsService: DocumentDetailsService,
    private authenticationService: AuthenticationService,
    private httpClient: HttpClient,
    private configAction: ConfigurationActions,
    private toastaService: ToastaService,
    private translateService: TranslateService,
  ) {
    this.initNexiaBasketStore();
  }

  private initNexiaBasketStore(): void {
    const storageDataByUserId: string = localStorage.getItem('' + this.authenticationService.getUserCredentials().id);
    if (storageDataByUserId && storageDataByUserId.length) {
      this.nexiaBasketAction.loadSucceeded(JSON.parse(storageDataByUserId));
    }
  }

  private updateLocalStorage(): void {
    const nexiaBasketState: NexiaBasketState = this.ngRedux.getState().dynamicSubStores.nexiaBasket;
    localStorage.setItem('' + this.authenticationService.getUserCredentials().id, JSON.stringify(nexiaBasketState.datas));
  }

  private upsertNexiaBasketObjects(key: NexiaBasketObjectKeys, ids: string[]): void {
    const datasToSend: {[key: string]: NexiaBasketObject[]} = {};
    const nexiaBasketObjects: NexiaBasketObject[] = [];
    ids.forEach((id: string) => nexiaBasketObjects.push(new NexiaBasketObject(id)));
    datasToSend[key] = nexiaBasketObjects;
    this.nexiaBasketAction.loadSucceeded(datasToSend);
    this.updateLocalStorage();
  }

  public upsertNexiaBasketDocuments(ids: string[]): void {
    this.upsertNexiaBasketObjects(NexiaBasketObjectKeys.DOCUMENTS, ids);
  }

  public upsertNexiaBasketEntities(ids: string[]): void {
    this.upsertNexiaBasketObjects(NexiaBasketObjectKeys.ENTITIES, ids);
  }

  private treatRequestsForDocuments(requests$: Observable<DocumentDetails>[]): void {
    forkJoin( requests$ ).subscribe( ( docsDocumentDetails: DocumentDetails[] | HttpErrorResponse[]) => {
      const nexiaBasketObjects: NexiaBasketObject[] = [];
      docsDocumentDetails.forEach((docDetailsResp: DocumentDetails | HttpErrorResponse) => {
        if ( docDetailsResp instanceof HttpErrorResponse ) {
          const error: HttpErrorResponse = docDetailsResp;
          const requestIdOnError = error.url.substr( error.url.lastIndexOf( '/' ) + 1 );
          const nexiaBasketObjectOnError: NexiaBasketObject = new NexiaBasketObject( requestIdOnError );
          nexiaBasketObjectOnError.loadedStatus = 404;
          nexiaBasketObjects.push( nexiaBasketObjectOnError );
        } else {
            const docDetails: DocumentDetails = docDetailsResp;
            const basketObjectToUpdate: NexiaBasketObject = new NexiaBasketObject( docDetails.details.cdx_id );
            basketObjectToUpdate.details = docDetails.details;
            basketObjectToUpdate.loadedStatus = 200;
            nexiaBasketObjects.push( basketObjectToUpdate );
        }
      });

      const datasToUpdate: {[key: string]: NexiaBasketObject[]} = {};
      datasToUpdate[NexiaBasketObjectKeys.DOCUMENTS] = nexiaBasketObjects;
      this.nexiaBasketAction.setNexiaBasketObjectsDetails(datasToUpdate);
    });
  }

  private treatRequestsForEntities(): void {}

  public fillDetails(objectKey: NexiaBasketObjectKeys): void {
    const nexiaBasketState: NexiaBasketState = _.cloneDeep(this.ngRedux.getState().dynamicSubStores.nexiaBasket);
    const nexiabasketStateDatas: NexiaBasketObject[] = nexiaBasketState.datas[objectKey] ? nexiaBasketState.datas[objectKey] : [];
    this.nexiaBasketAction.loadStarted();
    try {
      if ( !!nexiaBasketState && !!nexiaBasketState.datas && nexiabasketStateDatas.length > 0) {
        const requests$: Observable<DocumentDetails | Entity | HttpErrorResponse>[] = [];
        if (objectKey === NexiaBasketObjectKeys.DOCUMENTS) {
           // const requestsOnError: string[] = [];
            nexiabasketStateDatas.forEach( ( basketDocument: NexiaBasketObject ) => {
              requests$.push( this.documentDetailsService.getDocDetailsById(basketDocument.id)
                .pipe(catchError((error: HttpErrorResponse) => {
                  // requestsOnError.push('' + error.url.substr(error.url.lastIndexOf('/') + 1));
                  return of(error); })) );
            });
            this.treatRequestsForDocuments(requests$ as Observable<DocumentDetails>[]);
        } else {
          // TODO treat entities!!!!!
        }
      } else {
        throw new Error('No data in basket to search for ');
      }
    } catch (e) {
      this.nexiaBasketAction.loadFailed(e);
    }
  }

  public removeErrorFromBasketStore(): void {
    this.nexiaBasketAction.removeErrorFromStore();
  }

  public emptyNexiaBasketObjects( nexiaBasketObjectKey: NexiaBasketObjectKeys ): void {
    this.nexiaBasketAction.emptyNexiaBasketObjects(nexiaBasketObjectKey);
    this.updateLocalStorage();
  }

  public deleteNexiaObjects(nexiaObjectIds: string[], mode: DELETE_MODE = DELETE_MODE.SOFT): Observable<HttpResponseBase> {
    const headers = new HttpHeaders().set(HttpErrorInterceptor.BYPASS_HEADER, '');
    const body = {
      'mode': mode,
      'ids': nexiaObjectIds
    };
    return this.httpClient.post(
      Url.getProtectedApiBaseUrl(this.configAction) + Url.DOCUMENTS + Url.DELETE,
      body,
      {
        observe: 'response',
        headers: headers
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          this.cleanupBasket(nexiaObjectIds, response.body);
          this.toastaService.info(this.translateService.instant('TOASTER_MESSAGES.[Basket].DELETE_DOCUMENT', {okCount: nexiaObjectIds.length}));
        return response;
      }),
    catchError((response: HttpErrorResponse) => {
      const errCount = response?.error?.size;
      const okCount = nexiaObjectIds.length - errCount;
      if (errCount > 0) {
        this.toastaService.error(this.translateService.instant('TOASTER_MESSAGES.[Basket].FAIL_DELETE_DOCUMENT.' + (errCount === 1 ? 'singular' : 'plural'), {errCount: errCount, okCount: okCount}));
      } else {
        this.toastaService.error(response.message);
      }
      this.cleanupBasket(nexiaObjectIds, response.error);
      return of(response);
    }));
  }

  // Non : pas d'entrée lorsque pas d'erreur.
  private cleanupBasket(nexiaObjectIds: string[], responseBucket: any) {
    const failedIds = [];
    responseBucket?.documentErrors?.forEach(docErr => {
      failedIds.push(docErr.cdx_id);
      // if ( // inutile : la réponse ne contient une entrée que si il y a une erreur.
      //   Object.keys(docErr?.fields || {}).length === 0 &&
      //   Object.keys(docErr?.links || {}).length === 0 &&
      //   (docErr?.documents || []).length === 0
      // ) {
      // }
    });
    const okIds = nexiaObjectIds.filter(id => failedIds.indexOf(id) < 0);
      this.upsertNexiaBasketDocuments(okIds);
  }

}
